import {db_config, rd} from "../vars/rd";
import {$shutils} from "../libs/snnh/snnh";


export class DB {
    /** @type {{name: string, version: number, objectStoresMeta: {store: string, storeConfig: {keyPath: string, autoIncrement: boolean}, storeSchema: {name: string, keypath: string, options: {unique: boolean}}[]}[]}} */
    config;
    /** @type IDBDatabase */
    db;


    constructor(config) {
        this.config = config;
    }

    /**
     *
     * @returns {Promise<DB|string>}
     */
    open() {
        return new Promise((resolve, reject) => {
            const indexedDB = window.indexedDB || window['webkitIndexedDB'] || window['mozIndexedDB'] || window['moz_indexedDB'];
            const request = indexedDB.open(rd.app, this.config.version);
            request.onupgradeneeded = (e) => {
                const db = e.target.result;
                if(this.config.objectStoresMeta.length) {
                    for(let i = 0; i < this.config.objectStoresMeta.length; i++) {
                        const metadata = this.config.objectStoresMeta[i];
                        if(metadata) {
                            if(!db.objectStoreNames.contains(metadata.store)) {
                                /**
                                 * @type {IDBObjectStore}
                                 */
                                const store = db.createObjectStore(metadata.store, metadata.storeConfig);
                                // 인덱스 생성
                                if (metadata.storeSchema && metadata.storeSchema.length) {
                                    metadata.storeSchema.forEach((index) => {
                                        if (!store.indexNames.contains(index.name)) {
                                            store.createIndex(index.name, index.keyPath, index.options);
                                        }
                                    });
                                }
                            }
                        }
                    }
                }
            };

            request.onsuccess = (e) => {
                this.db = e.target.result;

                this.db.onversionchange = () => {
                    console.warn('Database version changed. Closing connection.');
                    this.close();
                };

                resolve(this);
            };

            request.onblocked = () => {
                console.warn('Database open request is blocked. Please close other database connections.');
            };

            request.onerror = (e) => {
                reject(e.target.error);
            };
        });
    }

    close() {
        if(this.db) this.db.close();
    }

    /**
     * @param storename
     * @param mode
     * @returns {{transaction: IDBTransaction, store: IDBObjectStore}}
     */
    store(storename, mode='readonly') {
        const transaction = this.db.transaction([storename], mode);
        const store = transaction.objectStore(storename);
        return {transaction, store};
    }

    /**
     *
     * @param storename
     * @param docs
     * @returns {Promise<unknown>}
     */
    insert(storename, docs={}) {
        return new Promise((resolve, reject) => {
            const {store} = this.store(storename, 'readwrite');
            const request = store.add(docs);

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = (e) => {
                reject(e.target.error);
            };
        });
    }

    /**
     *
     * @param storename
     * @param docsArray {*[]}
     * @returns {Promise<[]>}
     */
    insertBulk(storename, docsArray=[]) {
        return new Promise((resolve, reject) => {
            const {transaction, store} = this.store(storename, 'readwrite');

            const keys = [];

            if(!$shutils.isArray(docsArray)) {
                return this.insert(storename, docsArray);
            }

            docsArray.forEach(docs => {
                const request = store.add(docs);
                request.onsuccess = (e) => {
                    keys.push(e.target.result);
                }
                request.onerror = (e) => {
                    console.error('Failed to add docs in bulk operation:', e.target.error);
                };
            });

            transaction.oncomplete = () => {
                resolve(keys);
            };

            transaction.onerror = (e) => {
                console.error('Transaction failed during bulk add:', e.target.error);
                reject(e.target.error);
            };
        });
    }

    /**
     * @param storename
     * @param key {string}
     * @returns {Promise<unknown>}
     */
    async counts(storename, key) {
        return new Promise((resolve, reject) => {
            const {store} = this.store(storename, 'readonly');
            const request = store.count(key);

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = (e) => {
                console.error('Failed to update docs:', e.target.error);
                reject(e.target.error);
            };
        });
    }

    /**
     *
     * @param storename
     * @param whereFn {Function}
     * @returns {Promise<*>}
     */
    async select(storename, whereFn) {
        return new Promise(async resolve => {
            const docs = await this.selectBulk(storename, whereFn, 1);
            resolve(docs.length > 0 ? docs[0] : null);
        });
    }

    /**
     * @param storename
     * @param whereFn {Function}
     * @param limit {Number}
     * @returns {Promise<*>}
     */
    async selectBulk(storename, whereFn, limit=0) {
        return new Promise((resolve, reject) => {
            const {store} = this.store(storename);
            const results = [];
            const request = store.openCursor();

            request.onsuccess = (e) => {
                const cursor = e.target.result;
                if (cursor) {
                    const docs = cursor.value;
                    if(!(limit && limit === results.length)) {
                        if (whereFn(docs)) {
                            results.push(docs);
                        }
                    }
                    cursor.continue();
                } else {
                    resolve(results);
                }
            };

            request.onerror = (e) => {
                console.error('Failed to search by values:', e.target.error);
                reject(e.target.error);
            };
        });
    }


    async selectAll(storename) {
        return new Promise((resolve, reject) => {
            const {store} = this.store(storename);
            const request = store.getAll();

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = (e) => {
                reject(e.target.error);
            };
        });
    }

    async update(storename, docs={}) {

    }

    /**
     *
     * @param storename
     * @param parameters {*}
     * @param whereFn {Function}
     * @returns {Promise<unknown>}
     */
    async updateBulk(storename,  parameters, whereFn) {
        return new Promise((resolve, reject) => {
            const {store} = this.store([storename], 'readwrite');
            const request = store.openCursor();
            request.onsuccess = (e) => {
                /** @type {IDBCursorWithValue} */
                const cursor = e.target.result;
                if (cursor) {
                    const docs = cursor.value;
                    if (whereFn(docs)) {
                        for(const [key, value] of Object.entries(parameters)) {
                            docs[key] = value;
                        }
                        cursor.update(docs);
                    }
                    cursor.continue();
                } else {
                    resolve();
                }
            };

            request.onerror = (e) => {
                console.error('Failed to update docs by condition:', e.target.error);
                reject(e.target.error);
            };
        });
    }

    /**
     * @param storename
     * @param key {Number}
     * @returns {Promise<unknown>}
     */
    async delete(storename, key) {
        return new Promise((resolve, reject) => {
            const {store} = this.store(storename, 'readwrite');
            const request = store.delete(key);

            request.onsuccess = () => {
                resolve(key);
            };

            request.onerror = (e) => {
                console.error('Failed to update docs:', e.target.error);
                reject(e.target.error);
            };
        });
    }

    /**
     * @param storename
     * @param whereFn
     * @returns {Promise<unknown>}
     */
    async deleteBulk(storename, whereFn) {
        return new Promise((resolve, reject) => {
            const {store} = this.store(storename, 'readwrite');
            const request = store.openCursor();
            let count = 0;
            request.onsuccess = (e) => {
                /** @type {IDBCursorWithValue} */
                const cursor = e.target.result;
                if (cursor) {
                    const docs = cursor.value;
                    if (whereFn(docs)) {
                        cursor.delete();
                        count++;
                    }
                    cursor.continue();
                } else {
                    resolve(count);
                }
            };

            request.onerror = (e) => {
                console.error('Failed to update docs:', e.target.error);
                reject(e.target.error);
            };
        });
    }

    /**
     * 검색조건에 해당되는 데이터를 limits 개를 남기고 나머지를 지운다
     * @param storename
     * @param whereFn
     * @param limits
     * @returns {Promise<unknown>}
     */
    async deleteBulkRemain(storename, whereFn, limits=20) {
        return new Promise((resolve, reject) => {
            let count = 0;
            const {store} = this.store(storename, 'readwrite');
            const request = store.openCursor(null, "prev"); // prev => desc, next => asc
            request.onsuccess = (e) => {
                /** @type {IDBCursorWithValue} */
                const cursor = e.target.result;
                if (cursor) {
                    const docs = cursor.value;
                    if (whereFn(docs)) {
                        if(limits > count) {
                            count++;
                        } else {
                            cursor.delete();
                        }
                    }
                    cursor.continue();
                } else {
                    resolve(count);
                }
            };

            request.onerror = (e) => {
                console.error('Failed to update docs:', e.target.error);
                reject(e.target.error);
            };
        });
    }

    async deleteAll(storename) {
        return new Promise((resolve, reject) => {
            const {store} = this.store(storename, 'readwrite');
            const request = store.clear();

            request.onsuccess = () => {
                resolve();
            };

            request.onerror = (e) => {
                reject(e.target.error);
            };
        });
    }
}

export class DBHelperClass {
    _ins = null;
    /**
     *
     * @returns {Promise<DB>}
     * @constructor
     */
    async openDB() {
        if (!this._ins) {
            this._ins = new DB(db_config);
            await this._ins.open();
            return this._ins;
        }
        return this._ins;
    }

    closeDB() {
        if (this._ins) {
            this._ins.close();
            this._ins = null;
        }
    }
}


// WEBPACK FOOTER //
// ./src/modules/db.js